/**
 * Artwork FIXED-PRICE buyer settlement — end-to-end on-chain + backend orchestration.
 *
 * The buyer drives the whole forward-only spine (the fixed-price SELLER signs
 * nothing on-chain, §17):
 *   1. backend  POST /artworks/{id}/buy      → authority-signed OPEN permit
 *   2. wallet   USDC.approve(escrow, gross)  (only if allowance insufficient)
 *   3. wallet   ArtworkEscrow.open(permit, sig)   → funds locked
 *   4. indexer  EscrowOpened → ESCROWED       (poll GET /settlements/{sid})
 *   5. backend  POST /settlements/{sid}/authorize-release → release signature
 *   6. wallet   ArtworkEscrow.release(sid, releaseSig)    → payout finality
 *   7. indexer  EscrowReleased → PAYOUT_FINAL  (poll)
 *   8. backend  POST /settlements/{sid}/finalize          → ownership → buyer
 *
 * Nothing is fabricated: every economic fact + signature comes from the backend
 * authority; the connected wallet is the only broadcaster.
 */
import { useCallback, useRef, useState } from "react";
import { usePublicClient, useWriteContract } from "wagmi";

import { ERC20_APPROVE_ABI, isUserRejection } from "../../lib/usdc-approve";
import { ERC20_ALLOWANCE_ABI } from "./buy-settlement";
import { marketplaceApi } from "../../lib/marketplace-api";
import { ARTWORK_ESCROW_ABI, buildArtworkOpenPermit } from "./artwork-escrow-abi";

export type ArtworkBuyStage =
  | "prepare" | "allowance" | "approve" | "open" | "escrow_index"
  | "authorize" | "release" | "payout_index" | "finalize";

export type ArtworkBuyState =
  | { kind: "idle" }
  | { kind: "preparing" }
  | { kind: "checking_allowance" }
  | { kind: "awaiting_approve_wallet" }
  | { kind: "approving"; txHash: `0x${string}` }
  | { kind: "awaiting_open_wallet" }
  | { kind: "opening"; txHash: `0x${string}` }
  | { kind: "indexing_escrow" }
  | { kind: "authorizing_release" }
  | { kind: "awaiting_release_wallet" }
  | { kind: "releasing"; txHash: `0x${string}` }
  | { kind: "indexing_payout" }
  | { kind: "finalizing" }
  | { kind: "completed"; txHash: `0x${string}` }
  | { kind: "rejected"; stage: ArtworkBuyStage }
  | { kind: "reverted"; stage: ArtworkBuyStage; txHash: `0x${string}` }
  | { kind: "error"; stage: ArtworkBuyStage; error: string };

const PENDING: ReadonlyArray<ArtworkBuyState["kind"]> = [
  "preparing", "checking_allowance", "awaiting_approve_wallet", "approving",
  "awaiting_open_wallet", "opening", "indexing_escrow", "authorizing_release",
  "awaiting_release_wallet", "releasing", "indexing_payout", "finalizing",
];

export interface ArtworkBuyInput {
  artworkId: string;
  escrow: `0x${string}`;
  usdc: `0x${string}`;
  buyer: `0x${string}`;
  chainId: number;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface UseArtworkBuyResult {
  state: ArtworkBuyState;
  buy: (input: ArtworkBuyInput) => Promise<void>;
  reset: () => void;
  isPending: boolean;
}

export function useArtworkBuy(): UseArtworkBuyResult {
  const [state, setState] = useState<ArtworkBuyState>({ kind: "idle" });
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const runningRef = useRef(false);

  const isPending = PENDING.includes(state.kind);

  const reset = useCallback(() => {
    if (runningRef.current) return;
    setState({ kind: "idle" });
  }, []);

  const buy = useCallback(
    async (input: ArtworkBuyInput) => {
      if (runningRef.current) return;
      if (publicClient == null) {
        setState({ kind: "error", stage: "prepare", error: "public_client_not_ready" });
        return;
      }
      runningRef.current = true;
      const pc = publicClient;
      const waitReceipt = async (hash: `0x${string}`) => {
        const rc = await pc.waitForTransactionReceipt({ hash });
        return rc.status === "success" ? "success" : "reverted";
      };
      const pollUntil = async (
        sid: string,
        ok: (s: Awaited<ReturnType<typeof marketplaceApi.getSettlement>>) => boolean,
      ) => {
        const start = Date.now();
        while (Date.now() - start < 120_000) {
          const s = await marketplaceApi.getSettlement(sid);
          if (ok(s)) return s;
          await sleep(3000);
        }
        throw new Error("indexing_timeout");
      };

      let stage: ArtworkBuyStage = "prepare";
      try {
        // 1. backend prepare → OPEN permit
        stage = "prepare";
        setState({ kind: "preparing" });
        const prep = await marketplaceApi.buyArtwork(input.artworkId, {
          nonce: Date.now(), permit_ttl_seconds: 1800,
        });
        const sid = prep.settlement_id;
        const gross = BigInt(prep.gross_micro);

        // 2. allowance → approve exact gross when insufficient
        stage = "allowance";
        setState({ kind: "checking_allowance" });
        const allowance = (await pc.readContract({
          address: input.usdc, abi: ERC20_ALLOWANCE_ABI, functionName: "allowance",
          args: [input.buyer, input.escrow],
        })) as bigint;
        if (allowance < gross) {
          setState({ kind: "awaiting_approve_wallet" });
          let ah: `0x${string}`;
          try {
            ah = (await writeContractAsync({
              address: input.usdc, abi: ERC20_APPROVE_ABI, functionName: "approve",
              args: [input.escrow, gross], chainId: input.chainId,
            } as never)) as `0x${string}`;
          } catch (e) {
            setState(isUserRejection(e)
              ? { kind: "rejected", stage: "approve" }
              : { kind: "error", stage: "approve", error: errMsg(e) });
            return;
          }
          setState({ kind: "approving", txHash: ah });
          if ((await waitReceipt(ah)) !== "success") {
            setState({ kind: "reverted", stage: "approve", txHash: ah });
            return;
          }
        }

        // 3. open escrow (funds locked)
        setState({ kind: "awaiting_open_wallet" });
        let oh: `0x${string}`;
        try {
          oh = (await writeContractAsync({
            address: input.escrow, abi: ARTWORK_ESCROW_ABI, functionName: "open",
            args: [buildArtworkOpenPermit(prep.permit), prep.signature as `0x${string}`],
            chainId: input.chainId,
          } as never)) as `0x${string}`;
        } catch (e) {
          setState(isUserRejection(e)
            ? { kind: "rejected", stage: "open" }
            : { kind: "error", stage: "open", error: errMsg(e) });
          return;
        }
        setState({ kind: "opening", txHash: oh });
        if ((await waitReceipt(oh)) !== "success") {
          setState({ kind: "reverted", stage: "open", txHash: oh });
          return;
        }

        // 4. wait for ESCROWED projection
        stage = "escrow_index";
        setState({ kind: "indexing_escrow" });
        await pollUntil(sid, (s) => s.release_ready || s.finalize_ready || s.sold);

        // 5. backend authorize release
        stage = "authorize";
        setState({ kind: "authorizing_release" });
        const auth = await marketplaceApi.authorizeRelease(sid);

        // 6. release on-chain (payout finality)
        setState({ kind: "awaiting_release_wallet" });
        let rh: `0x${string}`;
        try {
          rh = (await writeContractAsync({
            address: input.escrow, abi: ARTWORK_ESCROW_ABI, functionName: "release",
            args: [prep.permit.settlementId as `0x${string}`, auth.release_signature as `0x${string}`],
            chainId: input.chainId,
          } as never)) as `0x${string}`;
        } catch (e) {
          setState(isUserRejection(e)
            ? { kind: "rejected", stage: "release" }
            : { kind: "error", stage: "release", error: errMsg(e) });
          return;
        }
        setState({ kind: "releasing", txHash: rh });
        if ((await waitReceipt(rh)) !== "success") {
          setState({ kind: "reverted", stage: "release", txHash: rh });
          return;
        }

        // 7. wait for PAYOUT_FINAL projection
        stage = "payout_index";
        setState({ kind: "indexing_payout" });
        await pollUntil(sid, (s) => s.finalize_ready || s.sold);

        // 8. finalize beneficial ownership
        stage = "finalize";
        setState({ kind: "finalizing" });
        await marketplaceApi.finalizeSettlement(sid);

        setState({ kind: "completed", txHash: rh });
      } catch (e) {
        setState({ kind: "error", stage, error: errMsg(e) });
      } finally {
        runningRef.current = false;
      }
    },
    [publicClient, writeContractAsync],
  );

  return { state, buy, reset, isPending };
}
